<template>
  <div>
    <!-- Start Page Title -->
    <div class="page-title-area">
      <div class="d-table">
        <div class="d-table-cell">
          <div class="container">
            <h2>{{ $t('contact_contact_us_title') }}</h2>
          </div>
        </div>
      </div>
          
      <div class="shape1"><img src="/img/shape1.png" alt="shape"></div>
      <div class="shape2 rotateme"><img src="/img/shape2.svg" alt="shape"></div>
      <div class="shape3"><img src="/img/shape3.svg" alt="shape"></div>
      <div class="shape4"><img src="/img/shape4.svg" alt="shape"></div>
      <div class="shape5"><img src="/img/shape5.png" alt="shape"></div>
      <div class="shape6 rotateme"><img src="/img/shape4.svg" alt="shape"></div>
      <div class="shape7"><img src="/img/shape4.svg" alt="shape"></div>
      <div class="shape8 rotateme"><img src="/img/shape2.svg" alt="shape"></div>
    </div>
    <!-- End Page Title -->

    <!-- Start Contact Area -->
    <section class="contact-area ptb-80">
      <div class="container">
        <div class="row h-100 justify-content-center align-items-center">
          <div class="col-lg-6 col-md-12">
            <img src="/img/1.png" alt="image">
          </div>

          <div class="col-lg-6 col-md-12" :class="{ 'hide': submitted || errorSubmission }">
            <div class="row">
              <!-- Show name field only if user is not logged in -->
              <div v-if="!isLoggedIn" class="col-lg-12 col-md-12">
                <div class="form-group">
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="form.name" 
                    :placeholder="$t('contact_name')" 
                    :class="{ 'error-border': validationErrors.name }"
                  >
                  <div v-if="validationErrors.name" class="help-block with-errors">{{ validationErrors.name }}</div>
                </div>
              </div>
              <div v-else class="ml-3 mb-3">
                {{ username }}
              </div>

              <!-- Show email field only if user is not logged in -->
              <div v-if="!isLoggedIn" class="col-lg-12 col-md-12">
                <div class="form-group">
                  <input 
                    type="email" 
                    class="form-control" 
                    v-model="form.email" 
                    :placeholder="$t('contact_email')"
                    :class="{ 'error-border': validationErrors.email }"
                  >
                  <div v-if="validationErrors.email" class="help-block with-errors">{{ validationErrors.email }}</div>
                </div>
              </div>

              <div class="col-lg-12 col-md-6">
                <div class="form-group">
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="form.subject" 
                    :placeholder="$t('contact_subject')"
                    :class="{ 'error-border': validationErrors.subject }"
                  >
                  <div v-if="validationErrors.subject" class="help-block with-errors">{{ validationErrors.subject }}</div>
                </div>
              </div>

              <div class="col-lg-12 col-md-12">
                <div class="form-group">
                  <textarea 
                    class="form-control" 
                    cols="30" 
                    rows="5" 
                    v-model="form.message" 
                    :placeholder="$t('contact_message')"
                    :class="{ 'error-border': validationErrors.message }"
                  ></textarea>
                  <div v-if="validationErrors.message" class="help-block with-errors">{{ validationErrors.message }}</div>
                </div>
              </div>

              <div class="col-lg-12 col-md-12">
                <button 
                  type="submit" 
                  class="btn btn-primary" 
                  @click="submitForm"
                  :disabled="isSubmitting"
                >
                  {{ isSubmitting ? 'Sending...' : $t('contact_button_send') }}
                </button>
                <div class="clearfix"></div>
              </div>
            </div>
          </div>

          <!-- Success message -->
          <div class="col-lg-6 col-md-12 contact-form-success" :class="{ 'hide': !submitted }">
            {{ $t('contact_success_p') }}
            <p><router-link to="/contact">{{ $t('back_to_form_p') }}</router-link></p>
          </div>

          <!-- Error message -->
          <div class="col-lg-6 col-md-12 contact-form-error" :class="{ 'hide': !errorSubmission }">
            {{ $t('contact_error_p') }}
            <p><router-link to="/contact">{{ $t('back_to_form_p') }}</router-link></p>
          </div>
        </div>
      </div>
    </section>
    <!-- End Contact Area -->
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { rest } from '@karpeleslab/klbfw'
import i18next from '../i18next.js'

export default {
  name: 'ContactPage',
  setup() {
    const isLoggedIn = ref(false)
    const username = ref('')
    const submitted = ref(false)
    const errorSubmission = ref(false)
    const isSubmitting = ref(false)
    
    // Form data
    const form = reactive({
      name: '',
      email: '',
      subject: '',
      message: ''
    })
    
    // Form validation
    const validationErrors = reactive({
      name: '',
      email: '',
      subject: '',
      message: ''
    })
    
    // Check if the user is logged in
    onMounted(() => {
      // This is a simplified approach - you would actually check with your auth system
      // For now, we'll just simulate it
      const userInfo = window._USER // This should be replaced with your actual auth check
      if (userInfo) {
        isLoggedIn.value = true
        username.value = userInfo.Display_Name || userInfo.Name || 'User'
      }
      
      // Set page title
      document.title = `${i18next.t('contact_contact_us_title')} - AtOnline.com`
    })
    
    // Form submission handler
    const submitForm = async () => {
      // Reset validation errors
      Object.keys(validationErrors).forEach(key => {
        validationErrors[key] = ''
      })
      
      // Validate form
      let isValid = true
      
      if (!isLoggedIn.value && !form.name.trim()) {
        validationErrors.name = 'Name is required'
        isValid = false
      }
      
      if (!isLoggedIn.value && !form.email.trim()) {
        validationErrors.email = 'Email is required'
        isValid = false
      } else if (!isLoggedIn.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        validationErrors.email = 'Please enter a valid email address'
        isValid = false
      }
      
      if (!form.subject.trim()) {
        validationErrors.subject = 'Subject is required'
        isValid = false
      }
      
      if (!form.message.trim()) {
        validationErrors.message = 'Message is required'
        isValid = false
      }
      
      if (!isValid) {
        return
      }
      
      // Submit form
      isSubmitting.value = true
      
      try {
        const to = "sppt-6m6dz2-263b-dd3i-5jbu-3pclndke" // Support ticket destination ID
        let payload
        
        if (isLoggedIn.value) {
          // For logged-in users - only need subject and message
          payload = {
            To: to,
            Subject: form.subject,
            Message: form.message
          }
        } else {
          // For anonymous users - need email, name, subject, message
          payload = {
            Email: form.email,
            Name: form.name,
            To: to,
            Subject: form.subject,
            Message: form.message
          }
        }
        
        await rest("Support/Ticket", "POST", payload)
        
        // Success
        submitted.value = true
        
        // Reset form
        form.name = ''
        form.email = ''
        form.subject = ''
        form.message = ''
      } catch (error) {
        console.error('Error submitting support request:', error)
        errorSubmission.value = true
      } finally {
        isSubmitting.value = false
      }
    }
    
    // Translation function aliased as $t to match Vue I18n syntax in template
    const $t = (key) => i18next.t(key)
    
    return {
      isLoggedIn,
      username,
      form,
      validationErrors,
      submitted,
      errorSubmission,
      isSubmitting,
      submitForm,
      $t
    }
  }
}
</script>

<style scoped>
.hide {
  display: none !important;
}

.error-border {
  border: 1px solid red !important;
}

.help-block {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.contact-form-success,
.contact-form-error {
  padding: 2rem;
  border-radius: 5px;
  margin-top: 1rem;
}

.contact-form-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.contact-form-error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}
</style>